import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BUSINESSES, LEARNING_TIPS, RIVAL } from "@/data/gameData";
import { useGame } from "@/context/GameContext";
import { Button } from "@/components/ui/button";
import HUD from "@/components/HUD";
import CustomerCard from "@/components/CustomerCard";
import LearningTip from "@/components/LearningTip";
import Shop from "@/components/Shop";
import DogWalkGame from "@/components/minigames/DogWalkGame";
import BabysitGame from "@/components/minigames/BabysitGame";
import CraftGame from "@/components/minigames/CraftGame";
import LawnGame from "@/components/minigames/LawnGame";
import { RefreshCw, ShoppingBag, Sparkles, Star, ArrowLeft, Megaphone, Printer, Tag } from "lucide-react";
import { toast } from "sonner";
import Mascot from "@/components/avatar/Mascot";
import Avatar from "@/components/avatar/Avatar";
import BrandBadge from "@/components/brand/BrandBadge";
import BrandEditor from "@/components/brand/BrandEditor";
import FlyerGame from "@/components/marketing/FlyerGame";
import FlyerDesigner from "@/components/marketing/FlyerDesigner";
import { Sound } from "@/lib/sound";

const MINIGAMES = {
  dog_walker: DogWalkGame,
  babysitter: BabysitGame,
  craft_maker: CraftGame,
  lawn_mower: LawnGame,
};

const Business = () => {
  const { id } = useParams();
  const biz = BUSINESSES[id];
  const navigate = useNavigate();
  const { state, refreshJobs, declineJob, completeJob, applyBoosts, loseJobToRival, isBusinessLocked, setPaywallOpen, getBrand } = useGame();
  const [searchParams] = useSearchParams();
  const locked = biz ? isBusinessLocked(id) : false;
  const brand = biz ? getBrand(id) : null;
  const [brandOpen, setBrandOpen] = useState(searchParams.get("tab") === "brand");
  const jobs = state.jobs[id] || [];
  const [activeJob, setActiveJob] = useState(null);
  const [activePrice, setActivePrice] = useState(0);
  const [activeCost, setActiveCost] = useState(0);
  const [warn, setWarn] = useState({ tooCheap: false, tooExpensive: false });
  const [tip, setTip] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [showResult, setShowResult] = useState(null);
  const [marketingOpen, setMarketingOpen] = useState(searchParams.get("tab") === "flyers");
  const [flyerOpen, setFlyerOpen] = useState(false);
  const [stolen, setStolen] = useState(null);

  useEffect(() => {
    if (!biz) navigate("/");
    else if (locked) { navigate("/"); setPaywallOpen(true); }
    // eslint-disable-next-line
  }, [biz, locked]);

  useEffect(() => {
    // auto-fill jobs if empty
    if (biz && !locked && jobs.length === 0 && !activeJob && !showResult) {
      refreshJobs(id);
      // Only on the very first business visit ever: one pricing tip
      if (state.totalJobs === 0 && !state.seenTips.includes("pricing")) {
        const pricingTip = LEARNING_TIPS.find((t) => t.id === "pricing");
        setTimeout(() => setTip(pricingTip), 800);
      }
    }
    // eslint-disable-next-line
  }, [id]);

  if (!biz || locked) return null;

  const MiniGame = MINIGAMES[id];
  const earn = state.earnings[id] || { revenue: 0, cost: 0, profit: 0, jobs: 0 };
  const today = state.todayEarnings[id] || { profit: 0, jobs: 0 };
  const leadsTomorrow = state.flyerLeads[id] || 0;
  const leadsToday = state.activeLeads[id] || 0;
  const marketingTip = LEARNING_TIPS.find((t) => t.id === "marketing");

  const handleStart = (job, opts) => {
    if (opts.stealChance > 0 && Math.random() < opts.stealChance) {
      loseJobToRival(id, job.id, opts.rivalPrice);
      setStolen({ job, price: opts.price, rivalPrice: opts.rivalPrice, repGap: state.reputation - state.rival.reputation });
      return;
    }
    setActiveJob(job);
    setActivePrice(opts.price);
    setActiveCost(opts.cost);
    setWarn({ tooCheap: opts.isTooCheap, tooExpensive: opts.isTooExpensive });
  };

  const handleGameFinish = ({ score, misses, rating }) => {
    // Adjust final based on pricing warnings
    let finalRating = rating;
    if (warn.tooExpensive) finalRating = Math.max(1, finalRating - 1);
    if (warn.tooCheap) finalRating = Math.min(5, finalRating + 0);
    // Apply supply boosts
    const boosted = applyBoosts(id, activePrice, activeCost);
    const finalPrice = boosted.price;
    const finalCost = boosted.cost;
    const finalRatingWithBoost = Math.min(5, finalRating + (boosted.ratingBoost || 0));

    completeJob({
      jobId: activeJob.id,
      businessId: id,
      price: finalPrice,
      cost: finalCost,
      rating: finalRatingWithBoost,
    });
    setShowResult({
      price: finalPrice,
      cost: finalCost,
      profit: finalPrice - finalCost,
      rating: finalRatingWithBoost,
      score,
      misses,
    });
    setActiveJob(null);
    toast.success(`Job complete! +$${finalPrice - finalCost} profit`);
  };

  return (
    <div className={`min-h-screen ${biz.lightBg}`} data-testid={`business-screen-${id}`}>
      <HUD />

      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${activeJob ? "py-3" : "py-6"}`}>
        {/* Header (hidden while a mini-game is running so the whole board fits on screen) */}
        {!activeJob && (
        <div className="flex items-start gap-3 mb-6">
          <button
            onClick={() => navigate("/")}
            className={`${biz.solidBg} ${biz.hoverBg} text-white rounded-2xl p-3 btn-3d border-2 border-black/10`}
            data-testid="back-to-hub-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className={`text-xs font-black uppercase tracking-widest ${biz.textColor} mb-1`}>
              {biz.emoji} {biz.name} · Business Dashboard
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <BrandBadge brand={brand} biz={biz} size="lg" showName={false} testid="business-brand" />
              <div>
                <h1 className="font-display text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-none break-words" data-testid="business-brand-name">
                  {brand.name}
                </h1>
                <button
                  onClick={() => { Sound.click(); setBrandOpen(true); }}
                  data-testid="brand-edit-btn"
                  className="mt-1 text-xs font-black uppercase tracking-wider text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
                >
                  <Tag className="w-3 h-3" /> Edit name &amp; logo
                </button>
              </div>
            </div>
            <p className="mt-2 text-slate-700 font-medium">{biz.tagline}</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="business-earnings-summary">
              <div className="bg-white border-2 border-emerald-300 rounded-xl px-3 py-1.5 text-xs font-heading font-black text-emerald-800">
                💰 Total profit <span className="text-base">${earn.profit}</span>
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs font-heading font-black text-slate-700">
                Earned ${earn.revenue} · Spent ${earn.cost} · {earn.jobs} {earn.jobs === 1 ? "job" : "jobs"}
              </div>
              <div className="bg-white border-2 border-amber-300 rounded-xl px-3 py-1.5 text-xs font-heading font-black text-amber-800" data-testid="business-today-earnings">
                Today: ${today.profit} · {today.jobs} {today.jobs === 1 ? "job" : "jobs"}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Active mini-game */}
        {activeJob && (
          <div className="mb-6 bg-white rounded-3xl border-4 border-amber-300 p-4 animate-pop-in" data-testid="minigame-container">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs font-black uppercase tracking-wider text-amber-700">
                  Mini-Game · {biz.minigameLabel}
                </div>
                <div className="font-heading font-black text-slate-900">
                  Job for {activeJob.name} · ${activePrice}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mascot pet={state.avatar.pet} size={52} cheer name={state.avatar.petName} testid="minigame-mascot" />
                <div className="text-3xl animate-bounce-slow hidden sm:block">{biz.emoji}</div>
              </div>
            </div>
            <MiniGame onFinish={handleGameFinish} />
          </div>
        )}

        {/* Result */}
        {showResult && (
          <div className="mb-6 bg-white rounded-3xl border-4 border-emerald-300 p-6 animate-pop-in text-center" data-testid="job-result">
            <div className="flex items-center justify-center gap-4 mb-2">
              <div className="text-6xl animate-bounce-slow">🎉</div>
              <Mascot pet={state.avatar.pet} size={72} cheer testid="result-mascot" />
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Job Complete!</h2>
            <div className="flex items-center justify-center gap-1 mb-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={`star-${i}`}
                  className={`w-5 h-5 ${i < showResult.rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl py-2">
                <div className="text-[10px] font-black uppercase text-emerald-700">Earned</div>
                <div className="font-display font-bold text-emerald-800">${showResult.price}</div>
              </div>
              <div className="bg-rose-50 border-2 border-rose-200 rounded-xl py-2">
                <div className="text-[10px] font-black uppercase text-rose-700">Cost</div>
                <div className="font-display font-bold text-rose-800">-${showResult.cost}</div>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl py-2">
                <div className="text-[10px] font-black uppercase text-blue-700">Profit</div>
                <div className="font-display font-bold text-blue-800">${showResult.profit}</div>
              </div>
            </div>
            <Button
              onClick={() => {
                setShowResult(null);
                if (!state.unlocked && !state.paywallSeen && state.totalJobs >= 1) setTimeout(() => setPaywallOpen(true), 400);
              }}
              data-testid="close-result-btn"
              className={`${biz.solidBg} ${biz.hoverBg} text-white font-black font-heading rounded-2xl btn-3d border-2 border-black/10 px-8 py-5`}
            >
              Next customer →
            </Button>
          </div>
        )}

        {/* Rival grabbed the job */}
        {stolen && !activeJob && !showResult && (
          <div className="mb-6 bg-white rounded-3xl border-4 border-orange-400 p-6 text-center animate-pop-in" data-testid="rival-steal-panel">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Avatar avatar={RIVAL.avatar} size={70} testid="steal-rival-avatar" />
              <div className="text-5xl">😮</div>
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">{RIVAL.name} grabbed this job!</h2>
            <p className="text-sm font-heading font-bold text-slate-700 mb-3" data-testid="rival-steal-reason">
              {stolen.job.name} chose {RIVAL.name}
              {stolen.price > stolen.rivalPrice && ` because Riley charged $${stolen.rivalPrice} and you asked $${stolen.price}`}
              {stolen.price > stolen.rivalPrice && stolen.repGap < 0 && " and"}
              {stolen.repGap < 0 && ` because Riley's rating (★${state.rival.reputation.toFixed(1)}) beat yours (★${state.reputation.toFixed(1)})`}
              .
            </p>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-3 text-xs font-bold text-blue-900 mb-4">
              💡 Lesson: customers compare! Keep prices close to the suggested price and earn great ratings so they pick you.
            </div>
            <Button onClick={() => { Sound.click(); setStolen(null); }} data-testid="rival-steal-close" className="bg-orange-500 hover:bg-orange-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-orange-600 px-8 py-5">
              I&apos;ll win the next one →
            </Button>
          </div>
        )}

        {/* Marketing mini-game */}
        {marketingOpen && !activeJob && !showResult && (
          <div className="mb-6">
            <FlyerGame
              businessId={id}
              tipSeen={state.seenTips.includes("marketing")}
              onLearn={() => setTip(marketingTip)}
              onDesign={() => setFlyerOpen(true)}
              onDone={() => setMarketingOpen(false)}
            />
          </div>
        )}

        {/* Actions */}
        {!activeJob && !showResult && !marketingOpen && (
          <>
            {(leadsToday > 0 || leadsTomorrow > 0) && (
              <div className="mb-4 flex flex-wrap gap-2" data-testid="marketing-banners">
                {leadsToday > 0 && (
                  <div className="bg-emerald-100 border-2 border-emerald-400 rounded-2xl px-3 py-2 text-sm font-heading font-black text-emerald-800" data-testid="leads-today-banner">
                    📣 {leadsToday} extra {leadsToday === 1 ? "customer" : "customers"} today from yesterday&apos;s flyers!
                  </div>
                )}
                {leadsTomorrow > 0 && (
                  <div className="bg-orange-100 border-2 border-orange-400 rounded-2xl px-3 py-2 text-sm font-heading font-black text-orange-800" data-testid="leads-tomorrow-banner">
                    📬 {leadsTomorrow} {leadsTomorrow === 1 ? "neighbor" : "neighbors"} will call tomorrow{leadsTomorrow >= 3 ? " (daily max!)" : ""}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-5">
              <Button
                onClick={() => refreshJobs(id)}
                data-testid="refresh-jobs-btn"
                className="bg-white hover:bg-slate-50 text-slate-900 font-heading font-black rounded-2xl btn-3d border-2 border-slate-300 py-5"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> New customers
              </Button>
              <Button
                onClick={() => setShopOpen(true)}
                data-testid="business-shop-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-orange-600 py-5"
              >
                <ShoppingBag className="w-4 h-4 mr-1" /> Shop supplies
              </Button>
              <Button
                onClick={() => {
                  const pool = LEARNING_TIPS.filter((t) => !state.seenTips.includes(t.id));
                  if (pool.length === 0) toast("You've mastered all lessons!");
                  else setTip(pool[0]);
                }}
                data-testid="learn-tip-btn"
                className="bg-blue-500 hover:bg-blue-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-blue-600 py-5"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Learn tip
              </Button>
              <Button
                onClick={() => { Sound.click(); setMarketingOpen(true); }}
                data-testid="marketing-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-amber-600 py-5"
              >
                <Megaphone className="w-4 h-4 mr-1" /> Hand out flyers
              </Button>
              <Button
                onClick={() => { Sound.click(); setFlyerOpen(true); }}
                data-testid="flyer-designer-btn"
                className="bg-rose-500 hover:bg-rose-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-rose-600 py-5"
              >
                <Printer className="w-4 h-4 mr-1" /> Make a flyer
              </Button>
            </div>

            {/* Customer requests */}
            {jobs.length === 0 ? (
              <div className="bg-white/80 border-4 border-dashed border-slate-300 rounded-3xl p-10 text-center" data-testid="no-jobs-state">
                <div className="text-5xl mb-3">📭</div>
                <p className="font-heading font-bold text-slate-600">No customer requests. Tap &ldquo;New customers&rdquo; to find some!</p>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {jobs.map((job) => (
                  <CustomerCard
                    key={job.id}
                    job={job}
                    onStart={handleStart}
                    onDecline={declineJob}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <LearningTip tip={tip} open={!!tip} onClose={() => setTip(null)} />
      <Shop open={shopOpen} onClose={() => setShopOpen(false)} businessFilter={id} />
      {flyerOpen && <FlyerDesigner businessId={id} open={flyerOpen} onClose={() => setFlyerOpen(false)} />}
      {brandOpen && <BrandEditor businessId={id} open={brandOpen} onClose={() => setBrandOpen(false)} />}
    </div>
  );
};

export default Business;
